import { bidPaymentStatusesEnum, documentsTrackingStatusesEnum, featureModuleTypesEnum } from "helpers/enums";
import { formatNumber, ymEvent } from "helpers/system";
import { requiredField } from "helpers/validators";
import moment from "moment";
import React from "react";

const requiredFieldValidators = [requiredField];
const noValidators = [];
const paymentDateFields = ["planPaymentDate", "invoiceDate", "factPaymentDate"];
const documentsTrackingDateFields = ["plannedArrivalDate", "documentsReceiptDate"];

const Payment = ({
    invoiceTriggerType,
    paymentTypeId,
    ndsTypeId,
    price,
    renderField,
    renderDateField,
    renderSelect,
    renderCheckbox,
    priceWithoutVatOnTopValue,
    isVatTopValue,
    priceWithoutVatOnTop,
    isVatTop,
    paymentPeriodType,
    paymentPeriodInDays,
    organization,
    paymentConfig,
    data,
    bidPolicy,
    availableFeatureModules,
    setValue,
}) => {
    const { routeCost, plannedNumberOfDays, estimatedNumberOfDays } = data;
    const {
        planPaymentDate,
        paymentStatus,
        remainingPayment,
        invoiceDate,
        comment,
        factPaymentDate,
        documentsTrackingStatus,
        plannedArrivalDate,
        documentsReceiptDate,
    } = paymentConfig;
    const paymentStatusValue = data.payment?.paymentStatus;
    const documentsTrackingStatusValue =
        data.payment?.documentsTrackingStatus || documentsTrackingStatusesEnum.NotShipped;
    const isPlanPaymentDateRequired =
        !!paymentStatusValue && paymentStatusValue !== bidPaymentStatusesEnum.NotPaid;
    const isRemainingPaymentRequired = paymentStatusValue === bidPaymentStatusesEnum.PartiallyPaid;
    const isDocumentsTrackingInTransit = documentsTrackingStatusValue === documentsTrackingStatusesEnum.InTransit;
    const isDocumentsTrackingDelivered = documentsTrackingStatusValue === documentsTrackingStatusesEnum.Delivered;
    const hasPlannedArrivalDate = isDocumentsTrackingInTransit || isDocumentsTrackingDelivered;

    const bidPrice = isVatTopValue ? priceWithoutVatOnTopValue || data.price : data.price;
    const margin = bidPrice - routeCost;
    const setPaymentValue = ({ name, value }) => {
        if ([...paymentDateFields, ...documentsTrackingDateFields].includes(name)) {
            value = value ? moment(value).format("YYYY-MM-DD") : null;
        }
        const newValue = { [name]: value };
        if (name === "paymentStatus") {
            ymEvent("Payment Status fields");

            if (value !== bidPaymentStatusesEnum.PartiallyPaid) {
                newValue.remainingPayment = null;
            }
            if (value !== bidPaymentStatusesEnum.Paid) {
                newValue.factPaymentDate = null;
            }
        }
        if (name === "documentsTrackingStatus") {
            if (![documentsTrackingStatusesEnum.InTransit, documentsTrackingStatusesEnum.Delivered].includes(value)) {
                newValue.plannedArrivalDate = null;
            }
            if (value !== documentsTrackingStatusesEnum.Delivered) {
                newValue.documentsReceiptDate = null;
            }
        }
        setValue({ name: "payment", value: { ...(data.payment ?? {}), ...newValue } });
    };

    return (
        <>
            <div className="form-group">
                <strong>Оплата</strong>
            </div>
            {renderSelect({ ...paymentTypeId, fieldWrapper: "col-sm-8 col-md-5" })}
            {renderSelect({ ...ndsTypeId, fieldWrapper: "col-sm-8 col-md-5" })}
            {renderSelect({ ...paymentPeriodType, fieldWrapper: "col-sm-8 col-md-5" })}
            {renderField({ ...paymentPeriodInDays, fieldWrapper: "col-sm-8 col-md-5" })}
            {isVatTopValue && priceWithoutVatOnTopValue
                ? renderField({
                      ...priceWithoutVatOnTop,
                      fieldWrapper: "col-sm-8 col-md-5",
                      bWrapper: "row form-group",
                      additionalText: organization?.currencyData?.sign,
                  })
                : renderField({
                      ...price,
                      fieldWrapper: "col-sm-8 col-md-5",
                      bWrapper: "row form-group",
                      additionalText: organization?.currencyData?.sign,
                  })}
            {renderCheckbox({
                ...isVatTop,
                fieldWrapper: "col-sm-8 col-md-5",
                bWrapper: "row form-group",
            })}
            {availableFeatureModules.includes(featureModuleTypesEnum.AccountsReceivable) && (
                <>
                    {renderSelect({ ...invoiceTriggerType, fieldWrapper: "col-sm-8 col-md-5" })}
                    {renderDateField({
                        ...invoiceDate,
                        onChange: setPaymentValue,
                        value: data.payment?.invoiceDate,
                        wrapper: "col-sm-8 col-md-5",
                    })}
                    {renderDateField({
                        ...planPaymentDate,
                        required: isPlanPaymentDateRequired,
                        validators: isPlanPaymentDateRequired ? requiredFieldValidators : noValidators,
                        onChange: setPaymentValue,
                        value: data.payment?.planPaymentDate,
                        wrapper: "col-sm-8 col-md-5",
                    })}
                    {renderSelect({
                        ...paymentStatus,
                        validators: data.payment?.updatedAt ? requiredFieldValidators : noValidators,
                        onChange: setPaymentValue,
                        value: data.payment?.paymentStatus,
                        fieldWrapper: "col-sm-8 col-md-5",
                    })}
                    {data.payment?.paymentStatus === bidPaymentStatusesEnum.Paid &&
                        renderDateField({
                            ...factPaymentDate,
                            validators: data.payment?.updatedAt ? requiredFieldValidators : noValidators,
                            onChange: setPaymentValue,
                            value: data.payment?.factPaymentDate,
                            wrapper: "col-sm-8 col-md-5",
                        })}
                    {data.payment?.paymentStatus === bidPaymentStatusesEnum.PartiallyPaid &&
                        renderField({
                            ...remainingPayment,
                            required: isRemainingPaymentRequired,
                            validators: isRemainingPaymentRequired ? requiredFieldValidators : noValidators,
                            onChange: setPaymentValue,
                            value: data.payment?.remainingPayment,
                            fieldWrapper: "col-sm-8 col-md-5",
                        })}
                    {renderSelect({
                        ...documentsTrackingStatus,
                        onChange: setPaymentValue,
                        value: documentsTrackingStatusValue,
                        fieldWrapper: "col-sm-8 col-md-5",
                        unClearable: true,
                    })}
                    {hasPlannedArrivalDate &&
                        renderDateField({
                            ...plannedArrivalDate,
                            onChange: setPaymentValue,
                            value: data.payment?.plannedArrivalDate,
                            wrapper: "col-sm-8 col-md-5",
                        })}
                    {isDocumentsTrackingDelivered &&
                        renderDateField({
                            ...documentsReceiptDate,
                            required: true,
                            validators: requiredFieldValidators,
                            onChange: setPaymentValue,
                            value: data.payment?.documentsReceiptDate,
                            wrapper: "col-sm-8 col-md-5",
                        })}
                    {renderField({
                        ...comment,
                        onChange: setPaymentValue,
                        value: data.payment?.comment,
                        fieldWrapper: "col-sm-8 col-md-5",
                    })}
                </>
            )}
            {bidPolicy.canSeeMarginality() && routeCost && (
                <div className="row form-group">
                    <div className="col-sm-8 col-md-5 offset-sm-4 offset-md-3">
                        <table className="table table-sm border-bottom border-secondary">
                            <tbody>
                                <tr>
                                    <td>Себестоимость маршрута</td>
                                    <td>
                                        {formatNumber(routeCost, {
                                            style: "currency",
                                            currency: organization?.currency,
                                        })}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Маржинальность</td>
                                    <td>
                                        {formatNumber(margin, {
                                            style: "currency",
                                            currency: organization?.currency,
                                        })}
                                    </td>
                                </tr>
                                {estimatedNumberOfDays && (
                                    <tr>
                                        <td>Маржинальность на день</td>
                                        <td>
                                            {formatNumber(margin / estimatedNumberOfDays, {
                                                style: "currency",
                                                currency: organization?.currency,
                                            })}
                                        </td>
                                    </tr>
                                )}
                                {plannedNumberOfDays && (
                                    <tr>
                                        <td>Прогноз маржинальности на день</td>
                                        <td>
                                            {formatNumber(margin / plannedNumberOfDays, {
                                                style: "currency",
                                                currency: organization?.currency,
                                            })}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
};

export default Payment;
